/**
 * "Open trouble ticket" dispatch warning.
 *
 * Open means NEW or PENDING — the two states someone still has to act on.
 * RESOLVED and CANCELLED are both closed, and counting CANCELLED would inflate
 * the number several times over. That definition is not restated here: the
 * backend's `attach_open_trouble_tickets` (app/utils/service_failure_tt.py) owns
 * it, and this module only consumes the `openTtCount` it produces. Keeping one
 * definition matters because tickets match an order two ways — by pronumber and
 * by bookingnumber/BOL — and on live orders 46% of the ones that fire are
 * BOL-only matches (prod 2026-09-30), which a naive pronumber check would miss.
 *
 * Shape note: the four sibling checks (dropRelease / notReceiving /
 * notRailbilled / missingPreadvise) each export a fixed MESSAGE constant plus a
 * `needsXWarning()` predicate. This one returns the message instead, because the
 * message carries the count — the useful part when a dispatcher is deciding
 * whether to look before assigning. Splitting it into predicate + builder would
 * force every call site to re-supply the count it just tested.
 *
 * Order-level, not leg-level: `cms.troubleticket` has no legnum/legrecno, so a
 * ticket belongs to the whole order — unlike the railbill/preadvise checks,
 * there is no leg the WARNING can be scoped to.
 *
 * The hard STOP below is positional all the same. An open rejection ticket is
 * about the re-delivery, so it blocks the legs that follow the rejection and
 * leaves the rejected group and everything before it alone: those already ran,
 * and reassigning a driver there changes nothing the ticket is waiting on.
 * Legs 1-2 of a delivered-then-rejected order were the reported symptom.
 *
 * A rejection is a POINT in the leg chain and the chain names it twice: the
 * destination of the leg that drove into it (`legAction2`) and the origin of the
 * leg that leaves it (`legtype`). The two agree — legtype[n] === legAction2[n-1]
 * — so legtype normally repeats what the leg before already said. It stops
 * repeating when that leg is gone: delete the legs that ran into the rejection
 * and the chain STARTS at it, with legtype the only column still carrying it
 * (PRO 5112523: REJECTED -> PICKUP -> DELIVERY). legtype therefore counts only
 * where nothing upstream accounts for it; taking it everywhere would also exempt
 * the leg LEAVING a rejection that closed its own group, which is the
 * disposition move the open ticket is about.
 *
 * Scope caveat on the dispatch boards: Smart Dispatch's `dispatchScopeLegs` and
 * the Planner's `row.legs` hold one dispatch group, not the order, so a
 * rejection in an EARLIER group is invisible to them and the leg stays blocked
 * — which is the right answer anyway, since it follows the rejection. Only a
 * rejection in a LATER group reads wrong, and then it over-blocks rather than
 * under-blocks; the backend guard, which always reads the full leg list, is the
 * authority either way.
 */

/**
 * The dispatch-warning line for an order's open-ticket count, or `null` when
 * there is nothing to warn about.
 *
 * `undefined` means the surface's endpoint does not run the enricher — unknown,
 * not zero — and is treated the same as none, so a surface that has not been
 * wired up stays silent rather than warning on every row.
 */
export function openTroubleTicketWarning(openTtCount?: number): string | null {
  if (typeof openTtCount !== 'number' || openTtCount <= 0) return null;
  return openTtCount === 1
    ? 'Warning: 1 open Trouble Ticket'
    : `Warning: ${openTtCount} open Trouble Tickets`;
}


const isRejected = (moveType?: string | null): boolean =>
  moveType?.trim().toUpperCase() === 'REJECTED';

/** Hard dispatch stop on the legs that follow a rejection; warnings remain. */
export function blockingTroubleTicketMessage(
  tickets?: { category: string; status: string }[],
  legAction2?: string | null,
  atOrBeforeRejectedGroup = false,
): string | null {
  if (atOrBeforeRejectedGroup || isRejected(legAction2)) return null;
  const ticket = tickets?.find((t) =>
    ['NEW', 'PENDING'].includes(t.status.trim().toUpperCase()) &&
    ['REJECTED', 'EMPTY RETURN REQUEST'].includes(t.category.trim().toUpperCase()),
  );
  return ticket
    ? `Cannot dispatch driver: open ${ticket.category} trouble ticket must be resolved first.`
    : null;
}


/**
 * True when a leg sits at or before the end of the order's rejected dispatch
 * group — the side of the rejection the ticket does not govern.
 *
 * Drop=Y closes a group, so the stopoff following a rejection is inside it, and
 * with more than one rejection the latest group's end wins: that is the one an
 * open ticket is about. A leg numbered past that end is the re-delivery the
 * ticket is holding, so it stays blocked.
 *
 * Pass the legs the user is LOOKING at, not the saved ones. On /pro that is
 * `allLegs` (`editing.effectiveLegs`), renumbered 1..N, so retyping a leg's move
 * type to REJECTED moves the boundary before anything is saved — matching the
 * backend guard, which overlays the same payload onto legsall.
 */
export function legAtOrBeforeRejectedGroup(
  legs: {
    legnum: number;
    legtype?: string | null;
    legAction2?: string | null;
    legDrop?: string | null;
  }[] | undefined,
  legnum?: number | null,
): boolean {
  if (!legs || legnum == null) return false;
  const ordered = [...legs].sort((a, b) => a.legnum - b.legnum);
  let end: number | null = null;
  let last: number | null = null;
  let rejected = false;
  // A list that does not open on leg 1 is a dispatch SCOPE, not the order: its
  // first leg has a predecessor we cannot see, so a REJECTED legtype there may be
  // repeating a rejection that is already upstream of this group. Assume it is —
  // the leg then stays blocked, which is the safe direction, and the backend
  // guard reads the whole chain either way.
  let previousWasRejected = ordered[0]?.legnum !== 1;
  for (const leg of ordered) {
    last = leg.legnum;
    const destinationRejected = isRejected(leg.legAction2);
    rejected ||= destinationRejected || (isRejected(leg.legtype) && !previousWasRejected);
    previousWasRejected = destinationRejected;
    if (leg.legDrop === 'Y') {
      if (rejected) end = leg.legnum;
      rejected = false;
    }
  }
  if (rejected && last != null) end = last;
  return end != null && legnum <= end;
}
